import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useParams } from "react-router-dom";
import type { AdminApi } from "../api/client.js";
import type { AppointmentDetail } from "../api/types.js";
import { Card } from "../components/Card.js";
import {
  StatusBadge,
  ModalityBadge,
  PatientTypeBadge,
} from "../components/StatusBadge.js";
import { Badge } from "../components/Badge.js";
import { ErrorBanner } from "../components/ErrorBanner.js";
import { formatDateTime, formatDob } from "../lib/format.js";
import { formatPhone } from "../lib/format-phone.js";

type Props = { api: AdminApi };

export function AppointmentDetailPage({ api }: Props) {
  const { id } = useParams<{ id: string }>();
  const queryClient = useQueryClient();
  const q = useQuery({
    enabled: !!id,
    queryKey: ["admin", "appointment", id],
    queryFn: () => api.getAppointment(id as string),
  });

  const cancelMutation = useMutation({
    mutationFn: (reason: string) =>
      api.cancelAppointment(id as string, reason),
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: ["admin", "appointment", id],
      });
      await queryClient.invalidateQueries({
        queryKey: ["admin", "appointments"],
      });
    },
  });

  async function openDocument(docId: string): Promise<void> {
    const { url } = await api.getDocumentViewUrl(docId);
    window.open(url, "_blank", "noopener,noreferrer");
  }

  if (q.isPending) {
    return <p className="text-sm text-slate-600">Loading…</p>;
  }
  if (q.isError || !q.data) {
    return <ErrorBanner message="Could not load appointment." />;
  }

  const appt = q.data;

  return (
    <div className="grid gap-4">
      <HeaderStrip
        appt={appt}
        onCancel={(reason) => cancelMutation.mutate(reason)}
        cancelling={cancelMutation.isPending}
      />
      {cancelMutation.isError ? (
        <ErrorBanner message="Could not cancel appointment." />
      ) : null}
      <div className="grid gap-4 lg:grid-cols-2">
        <PatientCard appt={appt} />
        <VisitCard appt={appt} />
      </div>
      {appt.status === "cancelled" ? (
        <CancellationCard appt={appt} />
      ) : null}
      <DocumentsCard appt={appt} onOpen={openDocument} />
    </div>
  );
}

function HeaderStrip({
  appt,
  onCancel,
  cancelling,
}: {
  appt: AppointmentDetail;
  onCancel: (reason: string) => void;
  cancelling: boolean;
}) {
  const [confirming, setConfirming] = useState(false);
  const [reason, setReason] = useState("");
  const isCancelled = appt.status === "cancelled";

  return (
    <Card>
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <div className="flex items-center gap-3">
            <span className="font-mono text-xl font-semibold uppercase">
              {appt.public_code}
            </span>
            <StatusBadge status={appt.status} />
          </div>
          <p className="mt-1 text-sm text-slate-600">
            {formatDateTime(appt.scheduled_start)} →{" "}
            {formatDateTime(appt.scheduled_end)}
          </p>
        </div>
        {!isCancelled ? (
          <div className="flex flex-col items-end gap-2">
            {confirming ? (
              <div className="flex w-80 flex-col gap-2">
                <textarea
                  className="field-input min-h-[64px]"
                  placeholder="Reason (required)"
                  value={reason}
                  onChange={(e) => setReason(e.target.value)}
                />
                <div className="flex justify-end gap-2">
                  <button
                    type="button"
                    onClick={() => {
                      setConfirming(false);
                      setReason("");
                    }}
                    className="rounded-md px-3 py-1 text-sm text-slate-700 hover:bg-slate-100"
                  >
                    Keep it
                  </button>
                  <button
                    type="button"
                    disabled={cancelling || !reason.trim()}
                    onClick={() => onCancel(reason.trim())}
                    className="rounded-md bg-rose-600 px-3 py-1 text-sm font-medium text-white disabled:opacity-50"
                  >
                    {cancelling ? "Cancelling…" : "Confirm cancel"}
                  </button>
                </div>
              </div>
            ) : (
              <button
                type="button"
                onClick={() => setConfirming(true)}
                className="rounded-md border border-rose-300 px-3 py-1 text-sm font-medium text-rose-700 hover:bg-rose-50"
              >
                Cancel appointment
              </button>
            )}
          </div>
        ) : null}
      </div>
    </Card>
  );
}

function PatientCard({ appt }: { appt: AppointmentDetail }) {
  return (
    <Card title="Patient">
      <dl className="grid gap-2 text-sm">
        <Row label="Name">
          {appt.patient_first_name} {appt.patient_last_name}
        </Row>
        <Row label="DOB">{formatDob(appt.patient_dob)}</Row>
        <Row label="Gender">{genderLabel(appt.patient_gender)}</Row>
        <Row label="Phone">{formatPhone(appt.patient_phone)}</Row>
        <Row label="Email">{appt.patient_email}</Row>
        <Row label="Address">
          <span className="whitespace-pre-line">
            {[
              appt.patient_address_street1,
              appt.patient_address_street2,
              `${appt.patient_address_city}, ${appt.patient_address_state} ${appt.patient_address_zip}`,
            ]
              .filter(Boolean)
              .join("\n")}
          </span>
        </Row>
      </dl>
    </Card>
  );
}

function VisitCard({ appt }: { appt: AppointmentDetail }) {
  return (
    <Card title="Visit">
      <dl className="grid gap-2 text-sm">
        <Row label="Visit type">{appt.visit_type_name}</Row>
        <Row label="Slug">
          <code className="text-xs">{appt.visit_type_slug}</code>
        </Row>
        <Row label="Modality">
          <ModalityBadge value={appt.modality} />
        </Row>
        <Row label="Patient type">
          <PatientTypeBadge value={appt.patient_type} />
        </Row>
        <Row label="Google event">
          {appt.gcal_event_id ? (
            <a
              href={`https://calendar.google.com/calendar/event?eid=${encodeURIComponent(
                appt.gcal_event_id,
              )}`}
              target="_blank"
              rel="noopener noreferrer"
              className="text-blue-700 hover:underline"
            >
              Open in Google Calendar
            </a>
          ) : (
            <span className="text-slate-500">Not synced</span>
          )}
        </Row>
      </dl>
    </Card>
  );
}

function CancellationCard({ appt }: { appt: AppointmentDetail }) {
  return (
    <Card title="Cancellation">
      <dl className="grid gap-2 text-sm">
        <Row label="When">
          {appt.cancelled_at ? formatDateTime(appt.cancelled_at) : "—"}
        </Row>
        <Row label="By">{appt.cancelled_by ?? "—"}</Row>
        <Row label="Reason">
          {appt.cancel_reason ? (
            <span className="italic">"{appt.cancel_reason}"</span>
          ) : (
            "—"
          )}
        </Row>
      </dl>
    </Card>
  );
}

function DocumentsCard({
  appt,
  onOpen,
}: {
  appt: AppointmentDetail;
  onOpen: (id: string) => void;
}) {
  if (appt.documents.length === 0) {
    return (
      <Card title="Documents">
        <p className="text-sm text-slate-600">No documents uploaded.</p>
      </Card>
    );
  }
  return (
    <Card title="Documents">
      <ul className="grid gap-2">
        {appt.documents.map((d) => (
          <li
            key={d.id}
            className="flex items-center justify-between rounded-md border border-slate-200 bg-white p-3 text-sm"
          >
            <div className="flex items-center gap-3">
              <Badge tone="slate">{d.kind}</Badge>
              <span className="text-slate-600">
                {d.content_type} · {(d.size_bytes / 1024).toFixed(1)} KB
              </span>
            </div>
            {d.purged_at ? (
              <span className="text-slate-500">
                Purged {formatDateTime(d.purged_at)}
              </span>
            ) : (
              <button
                type="button"
                onClick={() => onOpen(d.id)}
                className="rounded-md bg-blue-600 px-3 py-1 text-sm font-medium text-white hover:bg-blue-700"
              >
                View
              </button>
            )}
          </li>
        ))}
      </ul>
    </Card>
  );
}

function Row({
  label,
  children,
}: {
  label: string;
  children: React.ReactNode;
}) {
  return (
    <div className="grid grid-cols-[8rem_1fr] gap-3">
      <dt className="text-slate-500">{label}</dt>
      <dd className="text-slate-800">{children}</dd>
    </div>
  );
}

function genderLabel(g: AppointmentDetail["patient_gender"]): string {
  switch (g) {
    case "male":
      return "Male";
    case "female":
      return "Female";
    case "nonbinary":
      return "Non-binary";
    case "prefer_not_to_say":
      return "Prefer not to say";
  }
}
